/**
 * LE REFUS D'ECRITURE : une ecriture que l'app ne fera PAS, et la phrase qui
 * dit pourquoi.
 *
 * LE PROBLEME QU'IL FERME. Une case de fiche s'enregistre toute seule, et
 * l'app garde le dernier mot sur ce qu'elle accepte : un email doit avoir la
 * forme d'un email, le nom d'une societe ne s'efface pas. Jusqu'ici, ces
 * refus-la partaient comme n'importe quelle panne : la rangee 3 affichait
 * « Echec de l'enregistrement » avec un bouton « Reessayer » qui ne pouvait pas
 * reussir (rejouer un refus redonne un refus), et la phrase francaise de l'app
 * ne se lisait qu'en console, ou personne ne regarde. L'utilisateur voyait donc
 * une panne la ou il y avait une faute de frappe, et le seul geste qu'on lui
 * proposait etait le mauvais.
 *
 * CE QUE LE SOCLE NE PEUT PAS DEVINER, et pourquoi il faut ce marqueur. Un
 * `Error` attrape autour d'un `set()` peut etre n'importe quoi : une coupure
 * reseau, un refus des Security Rules, un bug. Afficher son `message` a
 * l'utilisateur donnerait « Missing or insufficient permissions » sur la
 * pastille, en anglais, sur un ecran francais. Une phrase ne s'affiche donc que
 * si l'app l'a ECRITE POUR ETRE LUE, et c'est ce que ce type dit, lui et rien
 * d'autre.
 *
 * CE QUI CHANGE A L'ECRAN (contrat `SaveState.reason`) : la phrase du refus
 * REMPLACE « Echec de l'enregistrement » et RETIRE « Reessayer ». Le cadre
 * rouge de la case, lui, reste : c'est elle qu'il faut corriger.
 *
 * COMMENT UNE APP S'EN SERT :
 *
 *   if (!FORME_EMAIL.test(texte)) refuser("L'email n'a pas la forme attendue (nom@domaine).");
 *
 * LA PHRASE EST CELLE DE L'APP, jamais du socle : elle nomme le champ dans les
 * mots du metier et dit quoi faire, pas ce qui a echoue.
 */

/**
 * LE MARQUEUR, POSE SUR L'INSTANCE ET NON SUR LA CLASSE, parce que
 * `instanceof` ne survit pas toujours au voyage : une app et le socle peuvent
 * se retrouver avec deux copies du module (deux `@taki/ui` dans un arbre de
 * dependances, un test qui recharge le paquet), et le refus redeviendrait
 * silencieusement une panne. Une propriete se lit dans tous les cas.
 */
const MARQUE = 'takiRefus';

/**
 * UN REFUS D'ECRITURE, a lancer depuis un `set()` (role ou colonne) ou depuis
 * un geste de fiche. Son `message` EST la phrase montree a l'utilisateur.
 */
export class RefusDEcriture extends Error {
  /** Le marqueur, du meme nom que `MARQUE` : c'est lui que `raisonDeRefus` lit. */
  readonly takiRefus = true;

  constructor(raison: string) {
    super(raison);
    this.name = 'RefusDEcriture';
  }
}

/**
 * REFUSER L'ECRITURE, avec sa phrase. Rend `never` : l'appelant n'a rien a
 * rendre apres, et TypeScript le sait (`refuser(...)` dans une branche ferme la
 * fonction comme un `throw`).
 */
export function refuser(raison: string): never {
  throw new RefusDEcriture(raison);
}

/**
 * LA PHRASE D'UN REFUS, ou `null` pour tout le reste.
 *
 * `null` COUVRE DEUX FAITS QUI SE VALENT ICI : ce n'etait pas un refus, ou
 * c'en etait un sans phrase (un refus muet ne vaut pas mieux que la panne
 * generique, donc il retombe dessus, avec son « Reessayer »).
 */
export function raisonDeRefus(cause: unknown): string | null {
  if (typeof cause !== 'object' || cause === null) return null;
  if ((cause as Record<string, unknown>)[MARQUE] !== true) return null;
  const message = (cause as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}
